import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { BehaviorSubject } from 'rxjs';
import { BadGuestsService } from '../services/bad-guests.service';
import { BadGuest } from '../models/BadGuest';
import { Country } from '../../../shared/address/Country';
import { State } from '../../../shared/address/State';
import { CountryService } from '../../../shared/reference/country.service';
import { ReferenceService } from '../../../shared/reference/reference.service';
import { Logger } from '../../../infrastructure/logging/logger';
import { BasicInfoStepComponent } from './components/basic-info-step/basic-info-step.component';
import { AddressStepComponent } from './components/address-step/address-step.component';

@Component({
  selector: 'app-bad-guest-form',
  templateUrl: './bad-guest-form.component.html',
  styleUrls: ['./bad-guest-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule, 
    MatIcon, 
    MatProgressSpinner, 
    MatExpansionModule,
    RouterModule,
    MatButtonModule,
    BasicInfoStepComponent, 
    AddressStepComponent
  ]
})
export class BadGuestFormComponent implements OnInit {

  basicInformationForm!: FormGroup;
  addressForm!: FormGroup;

  loading = new BehaviorSubject<boolean>(false);
  states!: State[];
  countries!: Country[];
  badGuestId: number = 0;
  selectedBadGuest!: BadGuest;
  existingBadGuest: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private badGuestsSerice: BadGuestsService,
    private router: Router,
    private countryService: CountryService,
    private referenceService: ReferenceService,
    private logService: Logger
  ) { }

  ngOnInit() {
    this.initForms();

    if (history.state.row) {
      this.selectedBadGuest = history.state.row;
      this.loadBadGuestFormData(this.selectedBadGuest);
    }
    this.loadPage();
  }

  async loadPage() {
    try {
      this.loading.next(true);
      this.states = this.referenceService.getAllStates();
      this.countries = await this.countryService.getCountries();
    } catch(e) {
      this.logService.handleError(e, 'An error occurred while retrieving Bad Guest form', 'An error occurred while retrieving Bad Guest form');
    } finally {
      if (this.loadingComplete()) {
        this.loading.next(false);
      }
    }
  }

  async loadBadGuestFormData(badGuest: any) {
    this.badGuestId = badGuest.id;
    this.existingBadGuest = true;
    
    this.basicInformationForm.patchValue(badGuest);
    this.basicInformationForm.get('birth_date')?.patchValue(new Date(badGuest.birth_date));
    this.basicInformationForm.markAllAsTouched();
    this.basicInformationForm.markAsDirty();

    this.addressForm.get('address_line_1')?.patchValue(badGuest.address_line_1);
    this.addressForm.get('address_line_2')?.patchValue(badGuest.address_line_2);
    this.addressForm.get('city')?.patchValue(badGuest.city);
    this.addressForm.get('state')?.patchValue(badGuest.state);
    this.addressForm.get('postal_code')?.patchValue(badGuest.postal_code);
    this.addressForm.get('country_id')?.patchValue(badGuest.country_id);
    this.addressForm.markAllAsTouched();
    this.addressForm.markAsDirty();
  }

  loadingComplete() {
    let countriesLoaded: boolean = this.countries !== undefined && this.countries !== null && this.countries.length > 0;
    return countriesLoaded;
  }

  initForms() {
    this.basicInformationForm = this.formBuilder.group({
      first_name: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      middle_name: [''],
      last_name: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      birth_date: [''],
      phone: ['', Validators.pattern(/^[\s()+-]*([0-9][\s()+-]*){10,11}(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/i)],
      email: ['', Validators.email],
      national_number: [''],
      ssn: [''],
      driver_license: [''],
      note: ['']
    });

    this.addressForm = this.formBuilder.group({
      address_line_1: [''],
      address_line_2: [''],
      city: [''],
      state: [''],
      postal_code: [''],
      country_id: ['']
    });
  }

  async submitBadGuest() {
    try {
      let badGuest = await this.buildBadGuest();

      if (this.existingBadGuest) {
        badGuest.address_id = this.selectedBadGuest.address_id;
        badGuest.address.address_id = this.selectedBadGuest.address_id;

        this.badGuestId = (await this.badGuestsSerice.updateBadGuest(this.badGuestId, badGuest)).id;
        this.navigateAfterSubmit(this.badGuestId);
      } else {
        let response = await this.badGuestsSerice.createBadGuest(badGuest);
        this.badGuestId = response.id;
        this.existingBadGuest = true;
        this.navigateAfterSubmit(this.badGuestId);
      }
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while submitting Bad Guest', 'An error occurred while submitting Bad Guest');
    }
  }

  buildBadGuest() {
    let badGuestPayload: any = {
      ...this.basicInformationForm.value,
    }
    badGuestPayload.address = this.addressForm.value;
    badGuestPayload.address.country_id = this.addressForm.get('country_id')?.value ? this.addressForm.get('country_id')?.value : 1;

    return badGuestPayload;
  }

  navigateAfterSubmit(badGuestId: any) {
    if (!badGuestId) {
      this.logService.handleError('An error occurred while submitting Bad Guest', 'An error occurred while submitting Bad Guest', 'An error occurred while submitting Bad Guest');
    } else {
      this.router.navigate([`/bad-guests`]);
    }
  }

  canCreateBadGuest(): boolean {
    return (
      this.basicInformationForm.valid
    );
  }
}